import Layout from '@/components/layout/Layout';
import { useAuthentication } from 'core/authentication/AuthenticationProvider';
import {
  fetchAuthenticatedNavigation,
  fetchFooterNavigation,
  fetchHomepage,
  fetchPublicNavigation,
} from 'core/strapi/StrapiApi';
import {
  IAuthenticatedWebsiteNavigation,
  IFooterNavigation,
  IHomepage,
  IPublicWebsiteNavigation,
} from 'core/strapi/types';
import { GetStaticPropsResult, NextPage } from 'next';
import Head from 'next/head';

type HomeProps = {
  navigationPublic: IPublicWebsiteNavigation;
  navigationAuthenticated: IAuthenticatedWebsiteNavigation;
  navigationFooter: IFooterNavigation;
  homepage: IHomepage;
};

const Home: NextPage<HomeProps> = ({
  navigationPublic,
  navigationAuthenticated,
  homepage,
  navigationFooter,
}) => {
  const { user } = useAuthentication();

  return (
    <Layout
      navigationPublic={navigationPublic}
      navigationAuthenticated={navigationAuthenticated}
      navigationFooter={navigationFooter}
    >
      <Head>
        <title>Contact Us | Click Against Hate</title>
      </Head>
      <main>
        <section className="hero">
          <div className="container tight-bottom-padding">
            <div className="row">
              <div className="copy col-5">
                <h1 tabIndex={-1}>Get in touch</h1>
                <p className="subtext">
                  Creating an Australia free of hate is a team effort. If you would like to be
                  involved with Click Against Hate, have any questions or would like to share
                  thoughts and feedback with us, we’d love to hear from you.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="panel one-col-center blue">
          <div className="container">
            <div className="row">
              <div className="copy">
                <p className="email-cta">
                  <span className="email-icon" aria-label="Email click against hate">
                    <i></i>
                  </span>
                  <a
                    href="mailto:help@clickagainsthate.org.au?subject=Click Against Hate"
                    className="h4"
                  >
                    help@clickagainsthate.org.au
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="cta-banner">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8">
                <h2>
                  Combating hate requires commitment.
                  <br />
                  That’s why Click Against Hate is provided{' '}
                  <span className="yellow">free of charge.</span>
                </h2>
                {!user && (
                  <a className="big btn" href="/account/register">
                    Get started today
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

// runs on build to generate content passed to main render function
export const getStaticProps = async (): Promise<GetStaticPropsResult<HomeProps>> => {
  // get navigation and homepage from CMS
  const [navigationPublic, navigationAuthenticated, homepage, navigationFooter]: [
    IPublicWebsiteNavigation,
    IAuthenticatedWebsiteNavigation,
    IHomepage,
    IFooterNavigation
  ] = await Promise.all([
    fetchPublicNavigation(),
    fetchAuthenticatedNavigation(),
    fetchHomepage(),
    fetchFooterNavigation(),
  ]);
  // pass on to page as properties
  return {
    props: {
      navigationPublic,
      navigationAuthenticated,
      homepage,
      navigationFooter,
    },
    revalidate: 1,
  };
};

export default Home;
